import classNames from 'classnames'
import { useRouter } from 'next/router'
import { NextSeoProps } from 'next-seo'
import { I18NConfig } from 'next/dist/server/config-shared'

import { Utils } from 'common/utils'
import { getPlans } from 'common/planData'
import { createClient } from '../prismicio'
import { PlanSlug } from 'common/planSlugs'
import { getFAQList } from 'common/cms-sdk'
import FAQList from 'components/util/FAQList'
import useShoppingBag from 'common/useShoppingBag'
import HeroBanner from 'components/hero/HeroBanner'
import standardiseTitle from 'project/standardiseTitle'
import WABenefits from 'components/WhatsApp/WABenefits'
import WhyTrustW4B from 'components/WhatsApp/WhyTrustW4B'
import WhoIsW4BFor from 'components/WhatsApp/WhoIsW4BFor'
import WhatsAppPrice from 'components/WhatsApp/WhatsAppPrice'
import LinkDevicesCard from 'components/WhatsApp/LinkDevices'
import VOPhoneService from 'components/WhatsApp/VOPhoneService'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import SeoContainer from 'components/util/containers/SeoContainer'
import HowWeWorkWithW4B from 'components/WhatsApp/HowWeWorkWithW4B'
import WAGetStartedSteps from 'components/WhatsApp/WAGetStartedSteps'
import { heroGap, sectionGap, sectionGapSmall } from 'project/padding'
import WABusinessFeatures from 'components/WhatsApp/WABusinessFeatures'
import EngageWithCustomers from 'components/WhatsApp/EngageWithCustomers'
import UnlockW4BFeaturesCard from 'components/WhatsApp/UnlockW4BFeatures'
import { TAddon, TFAQList, TPlanSummary, TReviews } from 'common/cms-types'
import { virtualOfficeOldStreetBreadcrumb } from 'components/util/containers/Breadcrumbs'
import { planPrice } from 'common/utils/planPrice'
import { ButtonType } from '../types/global'

export type Whatsapp4BType = {
  plans: TPlanSummary[]
  addons: TAddon[] | null
  faqList: TFAQList
  reviews: TReviews
}
const Whatsapp4BPage: NextPageWithLayout<Whatsapp4BType> = ({
  faqList,
  plans,
}) => {
  const router = useRouter()
  const { addPlan } = useShoppingBag()
  const planSummary = plans.find((v) => v.slug === PlanSlug.PHONE)!

  const onBuyClick = () => {
    addPlan(
      planSummary.slug,
      'year',
      planSummary,
      ButtonType.VIRTUAL_OFFICE_PHONE_PAGE_HERO,
    )
    router.push('/checkout')
  }

  const breadcrumb = virtualOfficeOldStreetBreadcrumb().concat([
    {
      item: `https://www.hoxtonmix.com/virtual-office-phone/`,
      name: `WhatsApp for Business Number`,
      position: 3,
    },
  ])

  const seoProps: NextSeoProps = {
    description: `Impress your clients with a dedicated WhatsApp for Business phone number from Hoxton Mix starting from ${planPrice
      .monthlyOnYearlyPlan(PlanSlug.PHONE)
      .toLocaleLowerCase()} a month.`,
    openGraph: {
      url: `https://www.hoxtonmix.com/virtual-office-phone/`,
    },
    title: standardiseTitle(
      `WhatsApp Business Phone Number for UK SMEs | Hoxton Mix`,
    ),
  }

  return (
    <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
      <main
        className={classNames(
          'overflow-x-hidden new-design d-flex flex-column',
          heroGap,
        )}
      >
        <HeroBanner
          robotImage={{
            alt: 'HaiRo™ robot mascot holding phone with whatsapp logo',
            dimensions: {
              height: 734,
              width: 657,
            },
            src: '/images/new/whatsapp/robot-holding-whatsapp.png',
          }}
          hideDownloadButtons
          hideFooter
          title={
            <>
              <h1 className='fs-fluid-32-64 lh-114 lh-lg-108 mb-lg-3 mb-3'>
                WhatsApp Business – connect with your customers instantly,
                anywhere in the world.
              </h1>
            </>
          }
          subtitleContainerClassName='mb-3 mb-lg-2'
          subtitle={
            <span className='fw-semibold d-block fs-fluid-24-26 lh-114'>
              Access professional customer-service tools and more with a
              personalised{' '}
              <span className='fw-bold'>
                WhatsApp for Business virtual phone number
              </span>{' '}
              from <span className='fw-bold'>Hoxton Mix.</span>
            </span>
          }
          description={
            <>
              <span className='fw-semibold fs-fluid-16-18 lh-123 d-none d-lg-block pe-5 mt-3 pb-7'>
                Build a flexible, world-class communication channel that lets
                you connect with customers instantly, from anywhere in the
                world.
                <br />
                <br />
                We provide WhatsApp-compatible virtual phone numbers available
                in UK mobile 07xxx formats, so you can communicate with
                customers professionally without sharing your personal mobile
                number.
              </span>
            </>
          }
          cta={
            <div className='d-none d-lg-block'>
              <span className='fs-20 fw-bold pb-2 d-block lh-140'>
                Connect with your customers directly, immediately and more
                effectively.
              </span>
              <WhatsAppPrice onBuyNow={onBuyClick} planSummary={planSummary} />
            </div>
          }
          customBottomContent={
            <div className='text-center'>
              <span className='fs-20 fw-bold pb-2 d-block lh-140'>
                Connect with your customers directly, immediately and more
                effectively.
              </span>
              <WhatsAppPrice onBuyNow={onBuyClick} planSummary={planSummary} />
            </div>
          }
        />

        <div
          className={classNames(
            'container d-flex flex-column gap-4 pt-lg-23',
            sectionGap,
          )}
        >
          <WAGetStartedSteps onBuyNow={onBuyClick} />
          <WABenefits />
          <EngageWithCustomers />
          <div className={classNames(sectionGapSmall, ' d-flex flex-column')}>
            <VOPhoneService onBuyNow={onBuyClick} />
            <LinkDevicesCard />
          </div>
          <WABusinessFeatures />
          <div className={classNames(sectionGapSmall, ' d-flex flex-column')}>
            <UnlockW4BFeaturesCard planSummary={planSummary} />
            <WhyTrustW4B />
            <WhoIsW4BFor />
          </div>
          <FAQList
            title={faqList.data.title}
            subtitle={faqList.data.subtitle}
            slices={faqList.data.slices}
            expandAllButton
            expandAllButtonDesktopOnly
          />
          <HowWeWorkWithW4B />
        </div>
      </main>
    </SeoContainer>
  )
}

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)

    const plans = await getPlans()
    const faqList = await getFAQList(
      client,
      lang,
      'whatsapp-business-virtual-phone',
    )

    return {
      props: {
        faqList,
        plans,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}

export default Whatsapp4BPage
